/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {RootState} from "../../store";
import {EAdasProgramSensorBomCheckboxTypes, TSensorLocations} from "../../../containers/pages/AdasPrograms/ItemDetails/types";
import cloneDeep from "lodash/cloneDeep";
import {Nullable} from "../../../utils/globalTypeUtils";
import {EAdasProgramsEditSensorDialogType} from "../../../containers/pages/AdasPrograms/ItemDetails/SensorBom/types";
import { ReportsTableData } from "../../../containers/pages/AdasPrograms/ItemDetails/common/constants";
import { fetchAdasPrograms } from "./thunks";
import { AdasProgram } from "./types";
import { EArgosReportsState } from "../../../constants/colors";
import { Dialog } from "../SensingSystemArchitectureDesign";

const checkboxData = [
  {
    label: 'Sensor locations',
    checked: false
  },
  {
    label: 'Cameras',
    checked: false
  },
  {
    label: 'Radars',
    checked: false
  },
  {
    label: 'Lidars',
    checked: false
  },
  {
    label: 'Ultrasonics',
    checked: false
  }
]

export type TCheckboxData = {
  [key in EAdasProgramSensorBomCheckboxTypes]: {
    checked: boolean
  }
}
const checkboxData2: TCheckboxData = {
  [EAdasProgramSensorBomCheckboxTypes.SENSOR_LOCATIONS]: {
    checked: true
  },
  [EAdasProgramSensorBomCheckboxTypes.CAMERAS]: {
    checked: true
  },
  [EAdasProgramSensorBomCheckboxTypes.RADARS]: {
    checked: true
  },
  [EAdasProgramSensorBomCheckboxTypes.LIDARS]: {
    checked: true
  },
  [EAdasProgramSensorBomCheckboxTypes.ULTRASONIC]: {
    checked: true
  }
}

type ISensorsFromDatabase = {
  [key in EAdasProgramSensorBomCheckboxTypes]: any[]
}

interface IMoveSensorBom {
  isMoveLocationsEnabled: boolean
  locationKey?: string
}
interface IEditSensorBom {
  moveSensorBom: IMoveSensorBom
  sensorLocations: Nullable<TSensorLocations>
}

export const defaultSelectSensorStepData: TSelectSensorStep = {
  greenlineName: null,
  locationKey: [],
  modality: '',
  name: '',
  version: '',
  id: '',
  processedResolution: null,
  notSourced: false,
  notes: '',
}


export interface IProcessedResolution {
  label: string
  width: number
  height: number
  mp: number
}

export interface TSelectSensorStep {
  /** Greenline name */
  greenlineName: string | null

  /** Location of sensor */
  locationKey: string[]

  /** Modality of sensor */
  modality: string

  /** Name of sensor, null if not sourced */
  name: string | null

  /** Version of sensor, null if not sourced */
  version: string | null

  /** ID of sensor, null if not sourced */
  id: string | null

  /**
   * Resolution of camera
   * Allowed values in range [0, inf)
   * null if modality is not camera
   */
  processedResolution: IProcessedResolution | null

  /** No name, version, or ID exists when true */
  notSourced: boolean

  /** Freeform text field */
  notes: string

  /** Old location, if editing */
  oldLocationKey?: string[]

  /** Old ID, if editing */
  oldId?: string
}

interface TextfieldState {
  /** The textfield value */
  value: string

  /** Description of error with the set value, null if value is valid */
  error: string | null
}

export const defaultDefinePropertiesStepData: TDefinePropertiesStep = {
  sensorDesiredFovDegrees: { value: "0", error: null },
  xNominal: { value: "0", error: null },
  xMinimum: { value: "0", error: null },
  xMaximum: { value: "0", error: null },
  yNominal: { value: "0", error: null },
  yMinimum: { value: "0", error: null },
  yMaximum: { value: "0", error: null },
  zNominal: { value: "0", error: null },
  zMinimum: { value: "0", error: null },
  zMaximum: { value: "0", error: null },
  yawNominal: { value: "0", error: null },
  yawMinimum: { value: "0", error: null },
  yawMaximum: { value: "0", error: null },
  pitchNominal: { value: "0", error: null },
  pitchMinimum: { value: "0", error: null },
  pitchMaximum: { value: "0", error: null },
  rollNominal: { value: "0", error: null },
  rollMinimum: { value: "0", error: null },
  rollMaximum: { value: "0", error: null },
  materialThicknessNominal: { value: "0", error: null },
  materialThicknessMinimum: { value: "0", error: null },
  materialThicknessMaximum: { value: "0", error: null },
  materialPerformanceDegradation: { value: "0", error: null },
  mountBuildToleranceToGroundElevation: { value: "0", error: null },
  mountBuildToleranceToGroundAzimuth: { value: "0", error: null },
  mountBuildToleranceToGroundRoll: { value: "0", error: null },
}

export interface TDefinePropertiesStep {
  sensorDesiredFovDegrees: TextfieldState
  xNominal: TextfieldState
  xMinimum: TextfieldState
  xMaximum: TextfieldState
  yNominal: TextfieldState
  yMinimum: TextfieldState
  yMaximum: TextfieldState
  zNominal: TextfieldState
  zMinimum: TextfieldState
  zMaximum: TextfieldState
  yawNominal: TextfieldState
  yawMinimum: TextfieldState
  yawMaximum: TextfieldState
  pitchNominal: TextfieldState
  pitchMinimum: TextfieldState
  pitchMaximum: TextfieldState
  rollNominal: TextfieldState
  rollMinimum: TextfieldState
  rollMaximum: TextfieldState
  materialThicknessNominal: TextfieldState
  materialThicknessMinimum: TextfieldState
  materialThicknessMaximum: TextfieldState
  materialPerformanceDegradation: TextfieldState
  mountBuildToleranceToGroundElevation: TextfieldState
  mountBuildToleranceToGroundAzimuth: TextfieldState
  mountBuildToleranceToGroundRoll: TextfieldState
}

export interface ConfigSensorWizardState {
  /** Event type that opened the wizard */
  action: EAdasProgramsEditSensorDialogType

  /** Data for Select Sensor step */
  selectSensorStep: TSelectSensorStep

  /** Data for Define Properties step */
  definePropertiesStep: TDefinePropertiesStep,

  greenlineDatasetId?: string
}

interface ISensorBom {
  sensorBomData: any // This is the item from the database. Don't make it anything else.
  hasSavedSensorBomData: boolean
  sensorBomAddSensorDialogOpen: boolean
  sensorBomEditSensorDialogOpen: boolean
  sensorBomAddSensorDialogState: any
  sensorBomEditSensorDialogState: any

  /** Data for Config Sensor wizard */
  configSensorWizardState: ConfigSensorWizardState | null

  sensorBomVisibleSensorsCheckboxes: TCheckboxData
  sensorsFromDatabase: ISensorsFromDatabase | null
  isReadOnly: any,
  vehProgs: any,
  editSensorBom: IEditSensorBom
}

export type TCoverageRequirementsLoadingState = {
  [key in EAdasProgramsLoadingStateTypes]?: {
   isLoading: boolean
   data?: any
  }
}

interface ICoverageRequirements {
  studies: any
  rightClickTreeNodeData: any
  blockHideRightClickMenu: boolean
  isRightClickMenuOpen: boolean
  argosJamaTreeData: any
  selectedLeftPanelTreeNode: any
  refreshAfterSync: any
  reportContent: any
  loadingState: TCoverageRequirementsLoadingState
  dialogs: ReportsDialogState
  table: ReportsTableState
}

interface IAdasProgramMetaData {
  adasProgramId: string
  programName: string
  modelYear: string
  fullProgramName: string
  isPlanOfRecord: boolean
  isProgramArchive: boolean
  creatorGmid: string
}

interface ReportsDialogState {
  manageDataSourcesDialogOpen: boolean
  newReportDialogOpen: boolean
  viewReportDialogOpen: boolean
  editReportDialogOpen: boolean
  updateReportDialogOpen: boolean
  deleteReportDialogOpen: boolean
  viewFullRequirementTextDialog: ViewFullRequirementTextDialog
}

interface ViewFullRequirementTextDialog extends Dialog {
  reportId: string | null
}

interface ReportsTableState {
  rows: ReportsTableData[]
  mergeConfigurations: string[]
  rowOrder: any

  /** Save button logic */
  isSaved: boolean

  /** Map of report ID to status */
  dataSetStatusMap: { [reportId: string]: string }

  selectedRows: any
}

interface SensorComponentRequirements {
  reportContent: any
  loadingState: TCoverageRequirementsLoadingState
  dialogs: ReportsDialogState
  table: ReportsTableState
}

interface WorldModelSolutionRequirements {
  reportContent: any
  loadingState: TCoverageRequirementsLoadingState
  dialogs: ReportsDialogState
  table: ReportsTableState
}

interface IState {
  programs: AdasProgram[]
  isFetchingPrograms: boolean
  adasProgramMetaData: IAdasProgramMetaData | null
  isEditLocationsEnabled: boolean
  sensorBom: ISensorBom
  readOnlyStates: any
  coverageRequirements: ICoverageRequirements
  sensorComponentRequirements: SensorComponentRequirements
  worldModelSolutionRequirements: WorldModelSolutionRequirements
}

export enum EAdasProgramsLoadingStateTypes {
  EDIT_REPORT = 'EDIT_REPORT',
  CREATE_REPORT = 'CREATE_REPORT',
  SYNC_REPORT = 'SYNC_REPORT',
  UNSYNC_REPORT = 'UNSYNC_REPORT',
  DELETE_REPORT = 'DELETE_REPORT',
}

export const enum EAdasProgramsSocketRequestTypes {
  UPDATE_TABLE_ROWS = 'updateTableRows',
  DELETE_TABLE_ROWS = 'deleteTableRows',

  /** Update the data set status map */
  DATA_SET_STATUS_MAP = 'updateReportsStatus',

  /** Update the table row order */
  TABLE_ROW_ORDER = 'coverageRequirementsReportsRowOrder',
}

const initialState: IState = {
  programs: [],
  isFetchingPrograms: false,
  adasProgramMetaData: null,
  isEditLocationsEnabled: false,
  sensorBom: {
    sensorBomData: null,
    hasSavedSensorBomData: true,
    sensorBomAddSensorDialogOpen: false,
    sensorBomEditSensorDialogOpen: false,
    sensorBomAddSensorDialogState: null,
    sensorBomEditSensorDialogState: null,
    configSensorWizardState: null,
    sensorBomVisibleSensorsCheckboxes: checkboxData2,
    sensorsFromDatabase: null,
    isReadOnly: null,
    vehProgs:[],
    editSensorBom: {
      moveSensorBom: {
        isMoveLocationsEnabled: false
      },
      sensorLocations: null
    }
  },
  readOnlyStates: {
    isVehicleProgramsReadOnly: false,
    isCoverageRequirementsReadOnly: false
  },
  coverageRequirements: {
    studies: null,
    rightClickTreeNodeData: null,
    blockHideRightClickMenu: false,
    isRightClickMenuOpen: false,
    argosJamaTreeData: {
      tree: null,
      mapJamaIdToArgosData: null
    },
    selectedLeftPanelTreeNode: null,
    refreshAfterSync: null,
    reportContent: null,
    loadingState: {
      [EAdasProgramsLoadingStateTypes.EDIT_REPORT]: {
        isLoading: false,
        data: null
      },
    },
    dialogs: {
      manageDataSourcesDialogOpen: false,
      newReportDialogOpen: false,
      viewReportDialogOpen: false,
      editReportDialogOpen: false,
      updateReportDialogOpen: false,
      deleteReportDialogOpen: false,
      viewFullRequirementTextDialog: {
        isOpen: false,
        reportId: null,
      },
    },
    table: {
      rows: [],
      mergeConfigurations: [],
      rowOrder: null,
      isSaved: false,
      dataSetStatusMap: {},
      selectedRows: {}
    }
  },
  sensorComponentRequirements: {
    reportContent: null,
    loadingState: {
      [EAdasProgramsLoadingStateTypes.EDIT_REPORT]: {
        isLoading: false,
        data: null
      },
    },
    dialogs: {
      manageDataSourcesDialogOpen: false,
      newReportDialogOpen: false,
      viewReportDialogOpen: false,
      editReportDialogOpen: false,
      updateReportDialogOpen: false,
      deleteReportDialogOpen: false,
      viewFullRequirementTextDialog: {
        isOpen: false,
        reportId: null,
      },
    },
    table: {
      rows: [],
      mergeConfigurations: [],
      rowOrder: null,
      isSaved: false,
      dataSetStatusMap: {},
      selectedRows: {},
    },
  },
  worldModelSolutionRequirements: {
    reportContent: null,
    loadingState: {
      [EAdasProgramsLoadingStateTypes.EDIT_REPORT]: {
        isLoading: false,
        data: null
      },
    },
    dialogs: {
      manageDataSourcesDialogOpen: false,
      newReportDialogOpen: false,
      viewReportDialogOpen: false,
      editReportDialogOpen: false,
      updateReportDialogOpen: false,
      deleteReportDialogOpen: false,
      viewFullRequirementTextDialog: {
        isOpen: false,
        reportId: null,
      },
    },
    table: {
      rows: [],
      mergeConfigurations: [],
      rowOrder: null,
      isSaved: false,
      dataSetStatusMap: {},
      selectedRows: {},
    },
  }
} as IState

export function orderTableDataByReportOrder(tableData: any[], reportOrder: string[]): any[] {
  // Create a mapping of reportIds to their corresponding data rows
  const tableDataMap: { [key: string]: any } = {};
  tableData.forEach(row => {
    tableDataMap[row.reportId] = row;
  });

  // Initialize an array to hold the ordered data
  const orderedTableData: any[] = [];

  // Add items to the ordered array based on the reportOrder
  reportOrder.forEach(reportId => {
    if (tableDataMap[reportId]) {
      orderedTableData.push(tableDataMap[reportId]);
    }
  });

  // Add any remaining items that were not in the reportOrder
  tableData.forEach(row => {
    if (!reportOrder.includes(row.reportId)) {
      orderedTableData.push(row);
    }
  });

  return orderedTableData;
}

const slice = createSlice({
  name: 'adasPrograms',
  initialState,
  reducers: {
    clearAdasProgramsState: () => initialState,

    /**
     * Edit sensor bom
     */
    setIsEditLocationsEnabled: (state) => void (state.isEditLocationsEnabled = !state.isEditLocationsEnabled),
    setIsMoveLocationsEnabled: (state) => void (state.sensorBom.editSensorBom.moveSensorBom.isMoveLocationsEnabled = !state.sensorBom.editSensorBom.moveSensorBom.isMoveLocationsEnabled),
    setMoveLocationKey: (state, action: PayloadAction<string>) => void (state.sensorBom.editSensorBom.moveSensorBom.locationKey = action.payload),
    setSensorLocations: (state, action: PayloadAction<any>) => {
      if (action.payload?.sensorLocationData) {
        if (!state.sensorBom.editSensorBom.sensorLocations) state.sensorBom.editSensorBom.sensorLocations = {}
        state.sensorBom.editSensorBom.sensorLocations[action.payload.sensorLocationData.locationKey] = action.payload.sensorLocationData
      } else {
        state.sensorBom.editSensorBom.sensorLocations = action.payload
      }
    },

    setSensorBomData: (state, action: PayloadAction<any>) => void (state.sensorBom.sensorBomData = action.payload),
    setHasSavedSensorBomData: (state, action: PayloadAction<boolean>) => void (state.sensorBom.hasSavedSensorBomData = action.payload),
    setConfigSensorWizardState: (state, action: PayloadAction<any>) => void (state.sensorBom.configSensorWizardState = action.payload),
    setConfigSensorWizardSelectSensorStep: (state, action: PayloadAction<any>) => {
      if (state.sensorBom.configSensorWizardState) {
        state.sensorBom.configSensorWizardState.selectSensorStep = action.payload;
      }
    },
    updateConfigSensorWizardSelectSensorStep: (state, action: PayloadAction<any>) => {
      if (state.sensorBom.configSensorWizardState) {
        state.sensorBom.configSensorWizardState.selectSensorStep = {...state.sensorBom.configSensorWizardState.selectSensorStep, ...action.payload};
      }
    },
    setConfigSensorWizardDefinePropertiesStep: (state, action: PayloadAction<any>) => {
      if (state.sensorBom.configSensorWizardState) {
        state.sensorBom.configSensorWizardState.definePropertiesStep = action.payload;
      }
    },
    setSensorBomVisibleSensorsCheckboxes: (state, action: PayloadAction<any>) => void (state.sensorBom.sensorBomVisibleSensorsCheckboxes = action.payload),
    // setSensorBomDbData: (state, action: PayloadAction<any>) => void (state.sensorBom.sensorBomData.data = action.payload),
    setSensorsFromDatabase: (state, action: PayloadAction<ISensorsFromDatabase>) => void (state.sensorBom.sensorsFromDatabase = action.payload),
    /**
     * Coverage Requirements
     */
    setCoverageRequirementsStudies: (state, action: PayloadAction<any>) => void (state.coverageRequirements.studies = action.payload),
    setAdasProgramsCoverageRequirementsRightClickTreeNodeData: (state, action: PayloadAction<any>) => void (state.coverageRequirements.rightClickTreeNodeData = action.payload),
    setAdasProgramsCoverageRequirementsRightClickTreeNodeBlock: (state, action: PayloadAction<boolean>) => void (state.coverageRequirements.blockHideRightClickMenu = action.payload),
    setAdasProgramsCoverageRequirementsRightClickTreeNodeIsOpen: (state, action: PayloadAction<boolean>) => void (state.coverageRequirements.isRightClickMenuOpen = action.payload),

    setAdasProgramsArgosJamaTreeData: (state, action: PayloadAction<any>) => void (state.coverageRequirements.argosJamaTreeData = action.payload),
    setAdasProgramsArgosJamaTree: (state, action: PayloadAction<any>) => void (state.coverageRequirements.argosJamaTreeData.tree = action.payload),
    setAdasProgramsArgosJamaTreeMap: (state, action: PayloadAction<any>) => void (state.coverageRequirements.argosJamaTreeData.mapJamaIdToArgosData = action.payload),

    setSensingSystemRequirementsDialogState: (state, action: PayloadAction<any>) => void (state.coverageRequirements.dialogs = action.payload),
    setSensingSystemRequirementsManageDataSourcesDialogOpen: (state, action: PayloadAction<boolean>) => void (state.coverageRequirements.dialogs.manageDataSourcesDialogOpen = action.payload),
    setSensingSystemRequirementsNewReportDialogOpen: (state, action: PayloadAction<boolean>) => void (state.coverageRequirements.dialogs.newReportDialogOpen = action.payload),
    setSensingSystemRequirementsViewReportDialogOpen: (state, action: PayloadAction<boolean>) => void (state.coverageRequirements.dialogs.viewReportDialogOpen = action.payload),
    setSensingSystemRequirementsEditReportDialogOpen: (state, action: PayloadAction<boolean>) => void (state.coverageRequirements.dialogs.editReportDialogOpen = action.payload),
    setSensingSystemRequirementsUpdateReportDialogOpen: (state, action: PayloadAction<boolean>) => void (state.coverageRequirements.dialogs.updateReportDialogOpen = action.payload),
    setSensingSystemRequirementsDeleteReportDialogOpen: (state, action: PayloadAction<boolean>) => void (state.coverageRequirements.dialogs.deleteReportDialogOpen = action.payload),
    setSensingSystemRequirementsViewFullRequirementTextDialog: (state, action: PayloadAction<ViewFullRequirementTextDialog>) => void (state.coverageRequirements.dialogs.viewFullRequirementTextDialog = action.payload),
    openSensingSystemRequirementsViewFullRequirementTextDialog: (state, action: PayloadAction<Omit<ViewFullRequirementTextDialog, 'isOpen'>>) => {
      state.coverageRequirements.dialogs.viewFullRequirementTextDialog.isOpen = true
      state.coverageRequirements.dialogs.viewFullRequirementTextDialog.reportId = action.payload.reportId
    },
    closeSensingSystemRequirementsViewFullRequirementTextDialog: (state) => {
      state.coverageRequirements.dialogs.viewFullRequirementTextDialog.isOpen = false
      state.coverageRequirements.dialogs.viewFullRequirementTextDialog.reportId = null
    },

    setSensorComponentRequirementsState: (state, action: PayloadAction<any>) => void (state.sensorComponentRequirements = action.payload),

    setSensorComponentRequirementsReportContent: (state, action: PayloadAction<any>) => void (state.sensorComponentRequirements.reportContent = action.payload),
    setSensorComponentRequirementsLoadingState: (state, action: PayloadAction<TCoverageRequirementsLoadingState>) => {
      state.sensorComponentRequirements.loadingState = {...state.sensorComponentRequirements.loadingState, ...action.payload}
    },

    setSensorComponentRequirementsDialogState: (state, action: PayloadAction<any>) => void (state.sensorComponentRequirements.dialogs = action.payload),
    setSensorComponentRequirementsTableState: (state, action: PayloadAction<any>) => void (state.sensorComponentRequirements.table = action.payload),

    setSensorComponentRequirementsManageDataSourcesDialogOpen: (state, action: PayloadAction<boolean>) => void (state.sensorComponentRequirements.dialogs.manageDataSourcesDialogOpen = action.payload),
    setSensorComponentRequirementsNewReportDialogOpen: (state, action: PayloadAction<boolean>) => void (state.sensorComponentRequirements.dialogs.newReportDialogOpen = action.payload),
    setSensorComponentRequirementsViewReportDialogOpen: (state, action: PayloadAction<boolean>) => void (state.sensorComponentRequirements.dialogs.viewReportDialogOpen = action.payload),
    setSensorComponentRequirementsEditReportDialogOpen: (state, action: PayloadAction<boolean>) => void (state.sensorComponentRequirements.dialogs.editReportDialogOpen = action.payload),
    setSensorComponentRequirementsUpdateReportDialogOpen: (state, action: PayloadAction<boolean>) => void (state.sensorComponentRequirements.dialogs.updateReportDialogOpen = action.payload),
    setSensorComponentRequirementsDeleteReportDialogOpen: (state, action: PayloadAction<boolean>) => void (state.sensorComponentRequirements.dialogs.deleteReportDialogOpen = action.payload),
    setSensorComponentRequirementsViewFullRequirementTextDialog: (state, action: PayloadAction<ViewFullRequirementTextDialog>) => void (state.sensorComponentRequirements.dialogs.viewFullRequirementTextDialog = action.payload),
    openSensorComponentRequirementsViewFullRequirementTextDialog: (state, action: PayloadAction<Omit<ViewFullRequirementTextDialog, 'isOpen'>>) => {
      state.sensorComponentRequirements.dialogs.viewFullRequirementTextDialog.isOpen = true
      state.sensorComponentRequirements.dialogs.viewFullRequirementTextDialog.reportId = action.payload.reportId
    },
    closeSensorComponentRequirementsViewFullRequirementTextDialog: (state) => {
      state.sensorComponentRequirements.dialogs.viewFullRequirementTextDialog.isOpen = false
      state.sensorComponentRequirements.dialogs.viewFullRequirementTextDialog.reportId = null
    },

    setSensorComponentRequirementsTableRows: (state, action: PayloadAction<any>) => void (state.sensorComponentRequirements.table.rows = action.payload),
    setSensorComponentRequirementsTableMergeConfigurations: (state, action: PayloadAction<any>) => void (state.sensorComponentRequirements.table.mergeConfigurations = action.payload),
    setSensorComponentRequirementsTableRowOrder: (state, action: PayloadAction<any>) => void (state.sensorComponentRequirements.table.rowOrder = action.payload),
    setSensorComponentRequirementsTableIsSaved: (state, action: PayloadAction<boolean>) => void (state.sensorComponentRequirements.table.isSaved = action.payload),
    setSensorComponentRequirementsTableDataSetStatusMap: (state, action: PayloadAction<any>) => {
      const {dataSetStatusMap} = action.payload
      state.sensorComponentRequirements.table.dataSetStatusMap = {...state.sensorComponentRequirements.table.dataSetStatusMap, ...dataSetStatusMap}
    },
    setSensorComponentRequirementsTableSelectedRows: (state, action: PayloadAction<any>) => void (state.sensorComponentRequirements.table.selectedRows = action.payload),
    updateSensorComponentRequirementsTable: (state, action: PayloadAction<any>) => {
      const {data, requestType} = action.payload

      switch (requestType) {
        case EAdasProgramsSocketRequestTypes.UPDATE_TABLE_ROWS: {
          const {rows} = data  /** { [reportId: string]: reportData/rowData} */

          /** Set the updated table rows and status map */
          const newTableData = cloneDeep(state.sensorComponentRequirements.table.rows)
          const dataSetStatusMap: Record<string, EArgosReportsState> = {}
          for (const reportId in rows) {
            const reportData = rows[reportId]
            dataSetStatusMap[reportId] = reportData.dataSetState
            const reportIndex = newTableData.findIndex((row) => row.reportId === reportId)
            if (reportIndex === -1) {
              newTableData.push(reportData)
            } else {
              newTableData[reportIndex] = reportData
            }
          }
          state.sensorComponentRequirements.table.rows = newTableData
          state.sensorComponentRequirements.table.dataSetStatusMap = {...state.sensorComponentRequirements.table.dataSetStatusMap, ...dataSetStatusMap}
          break
        }
        case EAdasProgramsSocketRequestTypes.DELETE_TABLE_ROWS: {
          const {reportIdsToDelete} = data  /** { [reportId: string] : boolean} */

          /** Remove deleted reports from rows */
          state.sensorComponentRequirements.table.rows = state.sensorComponentRequirements.table.rows.filter((row: any) => !reportIdsToDelete.hasOwnProperty(row.reportId))

          /** Remove deleted reports from the status map */
          const dataSetStatusMap = cloneDeep(state.sensorComponentRequirements.table.dataSetStatusMap)
          for (const reportId in reportIdsToDelete) {
            delete dataSetStatusMap[reportId]
          }
          state.sensorComponentRequirements.table.dataSetStatusMap = {...state.sensorComponentRequirements.table.dataSetStatusMap, ...dataSetStatusMap}
          break
        }
      }
    },

    setWorldModelSolutionRequirementsState: (state, action: PayloadAction<any>) => void (state.worldModelSolutionRequirements = action.payload),

    setWorldModelSolutionRequirementsReportContent: (state, action: PayloadAction<any>) => void (state.worldModelSolutionRequirements.reportContent = action.payload),
    setWorldModelSolutionRequirementsLoadingState: (state, action: PayloadAction<TCoverageRequirementsLoadingState>) => {
      state.worldModelSolutionRequirements.loadingState = {...state.worldModelSolutionRequirements.loadingState, ...action.payload}
    },

    setWorldModelSolutionRequirementsDialogState: (state, action: PayloadAction<any>) => void (state.worldModelSolutionRequirements.dialogs = action.payload),
    setWorldModelSolutionRequirementsTableState: (state, action: PayloadAction<any>) => void (state.worldModelSolutionRequirements.table = action.payload),

    setWorldModelSolutionRequirementsManageDataSourcesDialogOpen: (state, action: PayloadAction<boolean>) => void (state.worldModelSolutionRequirements.dialogs.manageDataSourcesDialogOpen = action.payload),
    setWorldModelSolutionRequirementsNewReportDialogOpen: (state, action: PayloadAction<boolean>) => void (state.worldModelSolutionRequirements.dialogs.newReportDialogOpen = action.payload),
    setWorldModelSolutionRequirementsViewReportDialogOpen: (state, action: PayloadAction<boolean>) => void (state.worldModelSolutionRequirements.dialogs.viewReportDialogOpen = action.payload),
    setWorldModelSolutionRequirementsEditReportDialogOpen: (state, action: PayloadAction<boolean>) => void (state.worldModelSolutionRequirements.dialogs.editReportDialogOpen = action.payload),
    setWorldModelSolutionRequirementsUpdateReportDialogOpen: (state, action: PayloadAction<boolean>) => void (state.worldModelSolutionRequirements.dialogs.updateReportDialogOpen = action.payload),
    setWorldModelSolutionRequirementsDeleteReportDialogOpen: (state, action: PayloadAction<boolean>) => void (state.worldModelSolutionRequirements.dialogs.deleteReportDialogOpen = action.payload),
    setWorldModelSolutionRequirementsViewFullRequirementTextDialog: (state, action: PayloadAction<ViewFullRequirementTextDialog>) => void (state.worldModelSolutionRequirements.dialogs.viewFullRequirementTextDialog = action.payload),
    openWorldModelSolutionRequirementsViewFullRequirementTextDialog: (state, action: PayloadAction<Omit<ViewFullRequirementTextDialog, 'isOpen'>>) => {
      state.worldModelSolutionRequirements.dialogs.viewFullRequirementTextDialog.isOpen = true
      state.worldModelSolutionRequirements.dialogs.viewFullRequirementTextDialog.reportId = action.payload.reportId
    },
    closeWorldModelSolutionRequirementsViewFullRequirementTextDialog: (state) => {
      state.worldModelSolutionRequirements.dialogs.viewFullRequirementTextDialog.isOpen = false
      state.worldModelSolutionRequirements.dialogs.viewFullRequirementTextDialog.reportId = null
    },

    setWorldModelSolutionRequirementsTableRows: (state, action: PayloadAction<any>) => void (state.worldModelSolutionRequirements.table.rows = action.payload),
    setWorldModelSolutionRequirementsTableMergeConfigurations: (state, action: PayloadAction<any>) => void (state.worldModelSolutionRequirements.table.mergeConfigurations = action.payload),
    setWorldModelSolutionRequirementsTableRowOrder: (state, action: PayloadAction<any>) => void (state.worldModelSolutionRequirements.table.rowOrder = action.payload),
    setWorldModelSolutionRequirementsTableIsSaved: (state, action: PayloadAction<boolean>) => void (state.worldModelSolutionRequirements.table.isSaved = action.payload),
    setWorldModelSolutionRequirementsTableDataSetStatusMap: (state, action: PayloadAction<any>) => {
      const {dataSetStatusMap} = action.payload
      state.worldModelSolutionRequirements.table.dataSetStatusMap = {...state.worldModelSolutionRequirements.table.dataSetStatusMap, ...dataSetStatusMap}
    },
    setWorldModelSolutionRequirementsTableSelectedRows: (state, action: PayloadAction<any>) => void (state.worldModelSolutionRequirements.table.selectedRows = action.payload),
    updateWorldModelSolutionRequirementsTable: (state, action: PayloadAction<any>) => {
      const {data, requestType} = action.payload

      switch (requestType) {
        case EAdasProgramsSocketRequestTypes.UPDATE_TABLE_ROWS: {
          const {rows} = data  /** { [reportId: string]: reportData/rowData} */

          /** Set the updated table rows and status map */
          const newTableData = cloneDeep(state.worldModelSolutionRequirements.table.rows)
          const dataSetStatusMap: Record<string, EArgosReportsState> = {}
          for (const reportId in rows) {
            const reportData = rows[reportId]
            dataSetStatusMap[reportId] = reportData.dataSetState
            const reportIndex = newTableData.findIndex((row) => row.reportId === reportId)
            if (reportIndex === -1) {
              newTableData.push(reportData)
            } else {
              newTableData[reportIndex] = reportData
            }
          }
          state.worldModelSolutionRequirements.table.rows = newTableData
          state.worldModelSolutionRequirements.table.dataSetStatusMap = {...state.worldModelSolutionRequirements.table.dataSetStatusMap, ...dataSetStatusMap}
          break
        }
        case EAdasProgramsSocketRequestTypes.DELETE_TABLE_ROWS: {
          const {reportIdsToDelete} = data  /** { [reportId: string] : boolean} */

          /** Remove deleted reports from rows */
          state.worldModelSolutionRequirements.table.rows = state.worldModelSolutionRequirements.table.rows.filter((row: any) => !reportIdsToDelete.hasOwnProperty(row.reportId))

          /** Remove deleted reports from the status map */
          const dataSetStatusMap = cloneDeep(state.worldModelSolutionRequirements.table.dataSetStatusMap)
          for (const reportId in reportIdsToDelete) {
            delete dataSetStatusMap[reportId]
          }
          state.worldModelSolutionRequirements.table.dataSetStatusMap = {...state.worldModelSolutionRequirements.table.dataSetStatusMap, ...dataSetStatusMap}
          break
        }
      }
    },

    setAdasProgramsMetaData: (state, action: PayloadAction<IAdasProgramMetaData>) => void (state.adasProgramMetaData = action.payload),
    setSelectedLeftPanelTreeNode: (state, action: PayloadAction<any>) => void (state.coverageRequirements.selectedLeftPanelTreeNode = action.payload),
    setRefreshAfterSync: (state, action: PayloadAction<any>) => {
      if(action.payload === null) {
        state.coverageRequirements.refreshAfterSync = null
        return
      }
      let oldRefreshAfterSync = cloneDeep(state.coverageRequirements.refreshAfterSync)
      // if(!oldRefreshAfterSync) oldRefreshAfterSync = {toggle: false, nodeId: null}
      if(!oldRefreshAfterSync) oldRefreshAfterSync = {}
      oldRefreshAfterSync.toggle = !oldRefreshAfterSync.toggle
      oldRefreshAfterSync.nodeId = action.payload.nodeId
      state.coverageRequirements.refreshAfterSync = oldRefreshAfterSync
    },
    setSensingSystemRequirementsReportContent: (state, action: PayloadAction<any>) => void (state.coverageRequirements.reportContent = action.payload),
    setSensingSystemRequirementsLoadingState: (state, action: PayloadAction<TCoverageRequirementsLoadingState>) => {
      state.coverageRequirements.loadingState = {...state.coverageRequirements.loadingState, ...action.payload}
    },
    setAdasCoverageRequirementsLoadingStateIsLoading: (state, action: PayloadAction<TCoverageRequirementsLoadingState>) => {
      state.coverageRequirements.loadingState = {...state.coverageRequirements.loadingState, ...action.payload}
    },

    setSensingSystemRequirementsReportsTableState: (state, action: PayloadAction<any>) => void (state.coverageRequirements.table = action.payload),

    setSensingSystemRequirementsTableRows: (state, action: PayloadAction<any>) => void (state.coverageRequirements.table.rows = action.payload),
    setSensingSystemRequirementsTableMergeConfigurations: (state, action: PayloadAction<any>) => void (state.coverageRequirements.table.mergeConfigurations = action.payload),
    setSensingSystemRequirementsTableRowOrder: (state, action: PayloadAction<any>) => void (state.coverageRequirements.table.rowOrder = action.payload),
    setSensingSystemRequirementsTableIsSaved: (state, action: PayloadAction<boolean>) => void (state.coverageRequirements.table.isSaved = action.payload),
    setSensingSystemRequirementsTableDataSetStatusMap: (state, action: PayloadAction<any>) => {
      const {dataSetStatusMap} = action.payload
      state.coverageRequirements.table.dataSetStatusMap = {...state.coverageRequirements.table.dataSetStatusMap, ...dataSetStatusMap}
    },
    setSensingSystemRequirementsTableSelectedRows: (state, action: PayloadAction<any>) => void (state.coverageRequirements.table.selectedRows = action.payload),
    updateSensingSystemRequirementsTable: (state, action: PayloadAction<any>) => {
      const {data, requestType} = action.payload

      switch (requestType) {
        case EAdasProgramsSocketRequestTypes.UPDATE_TABLE_ROWS: {
          const {rows} = data  /** { [reportId: string]: reportData/rowData} */

          /** Set the updated table rows and status map */
          const newTableData = cloneDeep(state.coverageRequirements.table.rows)
          const dataSetStatusMap: Record<string, EArgosReportsState> = {}
          for (const reportId in rows) {
            const reportData = rows[reportId]
            dataSetStatusMap[reportId] = reportData.dataSetState
            const reportIndex = newTableData.findIndex((row) => row.reportId === reportId)
            if (reportIndex === -1) {
              newTableData.push(reportData)
            } else {
              newTableData[reportIndex] = reportData
            }
          }
          state.coverageRequirements.table.rows = newTableData
          state.coverageRequirements.table.dataSetStatusMap = {...state.coverageRequirements.table.dataSetStatusMap, ...dataSetStatusMap}
          break
        }
        case EAdasProgramsSocketRequestTypes.DELETE_TABLE_ROWS: {
          const {reportIdsToDelete} = data  /** { [reportId: string] : boolean} */

          /** Remove deleted reports from rows */
          state.coverageRequirements.table.rows = state.coverageRequirements.table.rows.filter((row: any) => !reportIdsToDelete.hasOwnProperty(row.reportId))

          /** Remove deleted reports from the status map */
          const dataSetStatusMap = cloneDeep(state.coverageRequirements.table.dataSetStatusMap)
          for (const reportId in reportIdsToDelete) {
            delete dataSetStatusMap[reportId]
          }
          state.coverageRequirements.table.dataSetStatusMap = {...state.coverageRequirements.table.dataSetStatusMap, ...dataSetStatusMap}
          break
        }
      }
    },
    setSensorBomIsReadOnly: (state, action: PayloadAction<any>) => {
      const {adasProgramId, isReadOnly, isReadOnlyMap} = action.payload
      if (!state.sensorBom.isReadOnly) state.sensorBom.isReadOnly = {}
      if(isReadOnlyMap) {
        // append to the existing map
        state.sensorBom.isReadOnly = {...state.sensorBom.isReadOnly, ...isReadOnlyMap}
      } else {
        if (!state.sensorBom.isReadOnly) state.sensorBom.isReadOnly = {}
        state.sensorBom.isReadOnly[adasProgramId] = isReadOnly
      }
    },
    setIsVehicleProgramsReadOnly: (state, action: PayloadAction<any>) => {
      const isReadOnly = action.payload

      state.readOnlyStates.isVehicleProgramsReadOnly = isReadOnly
    },
    setIsCoverageRequirementsReadOnly: (state, action: PayloadAction<any>) => {
      const isReadOnly = action.payload
      state.readOnlyStates.isCoverageRequirementsReadOnly = isReadOnly
    },
    setSensorBomWarningVehIds: (state, action: PayloadAction<any>) => {
      const {adasProgramId, data} = action.payload
      if (!state.sensorBom.isReadOnly) state.sensorBom.isReadOnly = {}

      state.sensorBom.vehProgs[adasProgramId] = data
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdasPrograms.pending, (state) => {
        state.isFetchingPrograms = true
      })
      .addCase(fetchAdasPrograms.fulfilled, (state, action: PayloadAction<AdasProgram[]>) => {
        state.programs = action.payload
        state.isFetchingPrograms = false
      })
      .addCase(fetchAdasPrograms.rejected, (state, action) => {
        console.error(action.error.message)
        state.isFetchingPrograms = false
      })
  },
})

/**
 * Selectors
 */
const selectSensorBomState = (state: RootState) => state.adasPrograms.sensorBom
const selectSensorBomData = (state: RootState) => state.adasPrograms.sensorBom.sensorBomData
const selectConfigSensorWizardState = (state: RootState) => state.adasPrograms.sensorBom.configSensorWizardState
const selectConfigSensorWizardSelectSensorStep = (state: RootState) => state.adasPrograms.sensorBom.configSensorWizardState?.selectSensorStep
const selectConfigSensorWizardDefinePropertiesStep = (state: RootState) => state.adasPrograms.sensorBom.configSensorWizardState?.definePropertiesStep

const selectIsVehicleProgramReadOnlyState = (state: RootState) => state.adasPrograms.readOnlyStates.isVehicleProgramsReadOnly
const selectIsCoverageRequirementsReadOnlyState = (state: RootState) => state.adasPrograms.readOnlyStates.isCoverageRequirementsReadOnly
const selectAdasProgramsState = (state: RootState) => state.adasPrograms
const selectSensingSystemRequirementsState = (state: RootState) => state.adasPrograms.coverageRequirements

const selectAdasProgramsArgosJamaTree = (state: RootState) => state.adasPrograms.coverageRequirements.argosJamaTreeData.tree
const selectAdasProgramsArgosJamaTreeMap = (state: RootState) => state.adasPrograms.coverageRequirements.argosJamaTreeData.mapJamaIdToArgosData

const selectSensingSystemRequirementsReportContent = (state: RootState) => state.adasPrograms.coverageRequirements.reportContent
const selectSensingSystemRequirementsLoadingState = (state: RootState) => state.adasPrograms.coverageRequirements.loadingState
const selectSensingSystemRequirementsLoadingStateTypeIsLoading = (state: RootState, type: EAdasProgramsLoadingStateTypes) => state.adasPrograms.coverageRequirements.loadingState?.[type]?.isLoading ?? false

const selectSensingSystemRequirementsDialogState = (state: RootState) => state.adasPrograms.coverageRequirements.dialogs
const selectSensingSystemRequirementsTableState = (state: RootState) => state.adasPrograms.coverageRequirements.table

const selectSensingSystemRequirementsManageDataSourcesDialogOpen = (state: RootState) => state.adasPrograms.coverageRequirements.dialogs.manageDataSourcesDialogOpen
const selectSensingSystemRequirementsNewReportDialogOpen = (state: RootState) => state.adasPrograms.coverageRequirements.dialogs.newReportDialogOpen
const selectSensingSystemRequirementsViewReportDialogOpen = (state: RootState) => state.adasPrograms.coverageRequirements.dialogs.viewReportDialogOpen
const selectSensingSystemRequirementsEditReportDialogOpen = (state: RootState) => state.adasPrograms.coverageRequirements.dialogs.editReportDialogOpen
const selectSensingSystemRequirementsUpdateReportDialogOpen = (state: RootState) => state.adasPrograms.coverageRequirements.dialogs.updateReportDialogOpen
const selectSensingSystemRequirementsDeleteReportDialogOpen = (state: RootState) => state.adasPrograms.coverageRequirements.dialogs.deleteReportDialogOpen
const selectSensingSystemRequirementsViewFullRequirementTextDialog = (state: RootState) => state.adasPrograms.coverageRequirements.dialogs.viewFullRequirementTextDialog
const getSensingSystemRequirementsReportFromId = (state: RootState, reportId: string) => {
  const reportTable = state.adasPrograms.coverageRequirements.table.rows
  const report = reportTable.find((report) => report.reportId === reportId)
  return report === undefined ? null : report
}

const selectSensingSystemRequirementsTableRows = (state: RootState) => state.adasPrograms.coverageRequirements.table.rows
const selectSensingSystemRequirementsTableMergeConfigurations = (state: RootState) => state.adasPrograms.coverageRequirements.table.mergeConfigurations
const selectSensingSystemRequirementsTableRowOrder = (state: RootState) => state.adasPrograms.coverageRequirements.table.rowOrder
const selectSensingSystemRequirementsTableIsSaved = (state: RootState) => state.adasPrograms.coverageRequirements.table.isSaved
const selectSensingSystemRequirementsTableDataSetStatusMap = (state: RootState) => state.adasPrograms.coverageRequirements.table.dataSetStatusMap
const selectSensingSystemRequirementsTableSelectedRows = (state: RootState) => state.adasPrograms.coverageRequirements.table.selectedRows

const selectAdasProgramsMetaData = (state: RootState) => state.adasPrograms.adasProgramMetaData
const selectAdasProgramsArchive = (state: RootState) => selectAdasProgramsMetaData(state)?.isProgramArchive ?? false
const selectSensorComponentRequirementsState = (state: RootState) => state.adasPrograms.sensorComponentRequirements

const selectSensorComponentRequirementsReportContent = (state: RootState) => state.adasPrograms.sensorComponentRequirements.reportContent
const selectSensorComponentRequirementsLoadingState = (state: RootState) => state.adasPrograms.coverageRequirements.loadingState
const selectSensorComponentRequirementsLoadingStateTypeIsLoading = (state: RootState, type: EAdasProgramsLoadingStateTypes) => state.adasPrograms.sensorComponentRequirements.loadingState?.[type]?.isLoading ?? false

const selectSensorComponentRequirementsDialogState = (state: RootState) => state.adasPrograms.sensorComponentRequirements.dialogs
const selectSensorComponentRequirementsTableState = (state: RootState) => state.adasPrograms.sensorComponentRequirements.table

const selectSensorComponentRequirementsManageDataSourcesDialogOpen = (state: RootState) => state.adasPrograms.sensorComponentRequirements.dialogs.manageDataSourcesDialogOpen
const selectSensorComponentRequirementsNewReportDialogOpen = (state: RootState) => state.adasPrograms.sensorComponentRequirements.dialogs.newReportDialogOpen
const selectSensorComponentRequirementsViewReportDialogOpen = (state: RootState) => state.adasPrograms.sensorComponentRequirements.dialogs.viewReportDialogOpen
const selectSensorComponentRequirementsEditReportDialogOpen = (state: RootState) => state.adasPrograms.sensorComponentRequirements.dialogs.editReportDialogOpen
const selectSensorComponentRequirementsUpdateReportDialogOpen = (state: RootState) => state.adasPrograms.sensorComponentRequirements.dialogs.updateReportDialogOpen
const selectSensorComponentRequirementsDeleteReportDialogOpen = (state: RootState) => state.adasPrograms.sensorComponentRequirements.dialogs.deleteReportDialogOpen
const selectSensorComponentRequirementsViewFullRequirementTextDialog = (state: RootState) => state.adasPrograms.sensorComponentRequirements.dialogs.viewFullRequirementTextDialog
const getSensorComponentRequirementsReportFromId = (state: RootState, reportId: string) => {
  const reportTable = state.adasPrograms.sensorComponentRequirements.table.rows

  const report = reportTable.find((report) => report.reportId === reportId)
  return report === undefined ? null : report
}

const selectSensorComponentRequirementsTableRows = (state: RootState) => state.adasPrograms.sensorComponentRequirements.table.rows
const selectSensorComponentRequirementsTableMergeConfigurations = (state: RootState) => state.adasPrograms.sensorComponentRequirements.table.mergeConfigurations
const selectSensorComponentRequirementsTableRowOrder = (state: RootState) => state.adasPrograms.sensorComponentRequirements.table.rowOrder
const selectSensorComponentRequirementsTableIsSaved = (state: RootState) => state.adasPrograms.sensorComponentRequirements.table.isSaved
const selectSensorComponentRequirementsTableDataSetStatusMap = (state: RootState) => state.adasPrograms.sensorComponentRequirements.table.dataSetStatusMap
const selectSensorComponentRequirementsTableSelectedRows = (state: RootState) => state.adasPrograms.sensorComponentRequirements.table.selectedRows

const selectWorldModelSolutionRequirementsState = (state: RootState) => state.adasPrograms.worldModelSolutionRequirements

const selectWorldModelSolutionRequirementsReportContent = (state: RootState) => state.adasPrograms.worldModelSolutionRequirements.reportContent
const selectWorldModelSolutionRequirementsLoadingState = (state: RootState) => state.adasPrograms.worldModelSolutionRequirements.loadingState
const selectWorldModelSolutionRequirementsLoadingStateTypeIsLoading = (state: RootState, type: EAdasProgramsLoadingStateTypes) => state.adasPrograms.worldModelSolutionRequirements.loadingState?.[type]?.isLoading ?? false

const selectWorldModelSolutionRequirementsDialogState = (state: RootState) => state.adasPrograms.worldModelSolutionRequirements.dialogs
const selectWorldModelSolutionRequirementsTableState = (state: RootState) => state.adasPrograms.worldModelSolutionRequirements.table

const selectWorldModelSolutionRequirementsManageDataSourcesDialogOpen = (state: RootState) => state.adasPrograms.worldModelSolutionRequirements.dialogs.manageDataSourcesDialogOpen
const selectWorldModelSolutionRequirementsNewReportDialogOpen = (state: RootState) => state.adasPrograms.worldModelSolutionRequirements.dialogs.newReportDialogOpen
const selectWorldModelSolutionRequirementsViewReportDialogOpen = (state: RootState) => state.adasPrograms.worldModelSolutionRequirements.dialogs.viewReportDialogOpen
const selectWorldModelSolutionRequirementsEditReportDialogOpen = (state: RootState) => state.adasPrograms.worldModelSolutionRequirements.dialogs.editReportDialogOpen
const selectWorldModelSolutionRequirementsUpdateReportDialogOpen = (state: RootState) => state.adasPrograms.worldModelSolutionRequirements.dialogs.updateReportDialogOpen
const selectWorldModelSolutionRequirementsDeleteReportDialogOpen = (state: RootState) => state.adasPrograms.worldModelSolutionRequirements.dialogs.deleteReportDialogOpen
const selectWorldModelSolutionRequirementsViewFullRequirementTextDialog = (state: RootState) => state.adasPrograms.worldModelSolutionRequirements.dialogs.viewFullRequirementTextDialog
const getWorldModelSolutionRequirementsReportFromId = (state: RootState, reportId: string) => {
  const reportTable = state.adasPrograms.worldModelSolutionRequirements.table.rows
  const report = reportTable.find((report) => report.reportId === reportId)
  return report === undefined ? null : report
}

const selectWorldModelSolutionRequirementsTableRows = (state: RootState) => state.adasPrograms.worldModelSolutionRequirements.table.rows
const selectWorldModelSolutionRequirementsTableMergeConfigurations = (state: RootState) => state.adasPrograms.worldModelSolutionRequirements.table.mergeConfigurations
const selectWorldModelSolutionRequirementsTableRowOrder = (state: RootState) => state.adasPrograms.worldModelSolutionRequirements.table.rowOrder
const selectWorldModelSolutionRequirementsTableIsSaved = (state: RootState) => state.adasPrograms.worldModelSolutionRequirements.table.isSaved
const selectWorldModelSolutionRequirementsTableDataSetStatusMap = (state: RootState) => state.adasPrograms.worldModelSolutionRequirements.table.dataSetStatusMap
const selectWorldModelSolutionRequirementsTableSelectedRows = (state: RootState) => state.adasPrograms.worldModelSolutionRequirements.table.selectedRows

const selectEditSensorBomState = (state: RootState) => state.adasPrograms.sensorBom.editSensorBom
const selectMoveSensorLocationsState = (state: RootState) => state.adasPrograms.sensorBom.editSensorBom.moveSensorBom

const {actions, reducer} = slice

export {
  selectEditSensorBomState,
  selectMoveSensorLocationsState,

  selectSensorBomState,
  selectSensorBomData,
  selectConfigSensorWizardState,
  selectConfigSensorWizardSelectSensorStep,
  selectConfigSensorWizardDefinePropertiesStep,
  selectAdasProgramsState,
  selectSensingSystemRequirementsLoadingState,
  selectSensingSystemRequirementsLoadingStateTypeIsLoading,

  selectAdasProgramsArgosJamaTree,
  selectAdasProgramsArgosJamaTreeMap,

  selectSensingSystemRequirementsState,
  selectSensingSystemRequirementsReportContent,
  selectSensingSystemRequirementsDialogState,
  selectSensingSystemRequirementsTableState,
  selectSensingSystemRequirementsManageDataSourcesDialogOpen,
  selectSensingSystemRequirementsNewReportDialogOpen,
  selectSensingSystemRequirementsViewReportDialogOpen,
  selectSensingSystemRequirementsEditReportDialogOpen,
  selectSensingSystemRequirementsUpdateReportDialogOpen,
  selectSensingSystemRequirementsDeleteReportDialogOpen,
  selectSensingSystemRequirementsViewFullRequirementTextDialog,
  getSensingSystemRequirementsReportFromId,
  selectSensingSystemRequirementsTableRows,
  selectSensingSystemRequirementsTableMergeConfigurations,
  selectSensingSystemRequirementsTableRowOrder,
  selectSensingSystemRequirementsTableIsSaved,
  selectSensingSystemRequirementsTableDataSetStatusMap,
  selectSensingSystemRequirementsTableSelectedRows,

  selectAdasProgramsMetaData,
  selectAdasProgramsArchive,
  selectSensorComponentRequirementsState,
  selectSensorComponentRequirementsReportContent,
  selectSensorComponentRequirementsLoadingState,
  selectSensorComponentRequirementsLoadingStateTypeIsLoading,
  selectSensorComponentRequirementsDialogState,
  selectSensorComponentRequirementsTableState,
  selectSensorComponentRequirementsManageDataSourcesDialogOpen,
  selectSensorComponentRequirementsNewReportDialogOpen,
  selectSensorComponentRequirementsViewReportDialogOpen,
  selectSensorComponentRequirementsEditReportDialogOpen,
  selectSensorComponentRequirementsUpdateReportDialogOpen,
  selectSensorComponentRequirementsDeleteReportDialogOpen,
  selectSensorComponentRequirementsViewFullRequirementTextDialog,
  getSensorComponentRequirementsReportFromId,
  selectSensorComponentRequirementsTableRows,
  selectSensorComponentRequirementsTableMergeConfigurations,
  selectSensorComponentRequirementsTableRowOrder,
  selectSensorComponentRequirementsTableIsSaved,
  selectSensorComponentRequirementsTableDataSetStatusMap,
  selectSensorComponentRequirementsTableSelectedRows,

  selectWorldModelSolutionRequirementsState,
  selectWorldModelSolutionRequirementsReportContent,
  selectWorldModelSolutionRequirementsLoadingState,
  selectWorldModelSolutionRequirementsLoadingStateTypeIsLoading,
  selectWorldModelSolutionRequirementsDialogState,
  selectWorldModelSolutionRequirementsTableState,
  selectWorldModelSolutionRequirementsManageDataSourcesDialogOpen,
  selectWorldModelSolutionRequirementsNewReportDialogOpen,
  selectWorldModelSolutionRequirementsViewReportDialogOpen,
  selectWorldModelSolutionRequirementsEditReportDialogOpen,
  selectWorldModelSolutionRequirementsUpdateReportDialogOpen,
  selectWorldModelSolutionRequirementsDeleteReportDialogOpen,
  selectWorldModelSolutionRequirementsViewFullRequirementTextDialog,
  getWorldModelSolutionRequirementsReportFromId,
  selectWorldModelSolutionRequirementsTableRows,
  selectWorldModelSolutionRequirementsTableMergeConfigurations,
  selectWorldModelSolutionRequirementsTableRowOrder,
  selectWorldModelSolutionRequirementsTableIsSaved,
  selectWorldModelSolutionRequirementsTableDataSetStatusMap,
  selectWorldModelSolutionRequirementsTableSelectedRows,

  selectIsVehicleProgramReadOnlyState,
  selectIsCoverageRequirementsReadOnlyState
}
export const {

  setIsMoveLocationsEnabled,
  setMoveLocationKey,
  setSensorLocations,

  clearAdasProgramsState,

  setIsEditLocationsEnabled,
  setSensorBomData,
  setHasSavedSensorBomData,
  setConfigSensorWizardState,
  setConfigSensorWizardSelectSensorStep,
  updateConfigSensorWizardSelectSensorStep,
  setConfigSensorWizardDefinePropertiesStep,
  setSensorBomVisibleSensorsCheckboxes,
  setSensorsFromDatabase,
  setSensorBomIsReadOnly,
  setSensorBomWarningVehIds,

  setCoverageRequirementsStudies,
  setAdasProgramsCoverageRequirementsRightClickTreeNodeData,
  setAdasProgramsCoverageRequirementsRightClickTreeNodeBlock,
  setAdasProgramsCoverageRequirementsRightClickTreeNodeIsOpen,

  setAdasProgramsArgosJamaTreeData,
  setAdasProgramsArgosJamaTree,
  setAdasProgramsArgosJamaTreeMap,

  setSensingSystemRequirementsReportContent,
  setSensingSystemRequirementsLoadingState,
  setSensingSystemRequirementsDialogState,
  setSensingSystemRequirementsReportsTableState,
  setSensingSystemRequirementsManageDataSourcesDialogOpen,
  setSensingSystemRequirementsNewReportDialogOpen,
  setSensingSystemRequirementsViewReportDialogOpen,
  setSensingSystemRequirementsEditReportDialogOpen,
  setSensingSystemRequirementsUpdateReportDialogOpen,
  setSensingSystemRequirementsDeleteReportDialogOpen,
  setSensingSystemRequirementsViewFullRequirementTextDialog,
  openSensingSystemRequirementsViewFullRequirementTextDialog,
  closeSensingSystemRequirementsViewFullRequirementTextDialog,
  setSensingSystemRequirementsTableRows,
  setSensingSystemRequirementsTableMergeConfigurations,
  setSensingSystemRequirementsTableRowOrder,
  setSensingSystemRequirementsTableIsSaved,
  setSensingSystemRequirementsTableDataSetStatusMap,
  setSensingSystemRequirementsTableSelectedRows,

  setSensorComponentRequirementsState,
  setSensorComponentRequirementsReportContent,
  setSensorComponentRequirementsLoadingState,
  setSensorComponentRequirementsDialogState,
  setSensorComponentRequirementsTableState,
  setSensorComponentRequirementsManageDataSourcesDialogOpen,
  setSensorComponentRequirementsNewReportDialogOpen,
  setSensorComponentRequirementsViewReportDialogOpen,
  setSensorComponentRequirementsEditReportDialogOpen,
  setSensorComponentRequirementsUpdateReportDialogOpen,
  setSensorComponentRequirementsDeleteReportDialogOpen,
  setSensorComponentRequirementsViewFullRequirementTextDialog,
  openSensorComponentRequirementsViewFullRequirementTextDialog,
  closeSensorComponentRequirementsViewFullRequirementTextDialog,
  setSensorComponentRequirementsTableRows,
  setSensorComponentRequirementsTableMergeConfigurations,
  setSensorComponentRequirementsTableRowOrder,
  setSensorComponentRequirementsTableIsSaved,
  setSensorComponentRequirementsTableDataSetStatusMap,
  setSensorComponentRequirementsTableSelectedRows,
  updateSensorComponentRequirementsTable,

  setWorldModelSolutionRequirementsState,
  setWorldModelSolutionRequirementsLoadingState,
  setWorldModelSolutionRequirementsReportContent,
  setWorldModelSolutionRequirementsDialogState,
  setWorldModelSolutionRequirementsTableState,
  setWorldModelSolutionRequirementsManageDataSourcesDialogOpen,
  setWorldModelSolutionRequirementsNewReportDialogOpen,
  setWorldModelSolutionRequirementsViewReportDialogOpen,
  setWorldModelSolutionRequirementsEditReportDialogOpen,
  setWorldModelSolutionRequirementsUpdateReportDialogOpen,
  setWorldModelSolutionRequirementsDeleteReportDialogOpen,
  setWorldModelSolutionRequirementsViewFullRequirementTextDialog,
  openWorldModelSolutionRequirementsViewFullRequirementTextDialog,
  closeWorldModelSolutionRequirementsViewFullRequirementTextDialog,
  setWorldModelSolutionRequirementsTableRows,
  setWorldModelSolutionRequirementsTableMergeConfigurations,
  setWorldModelSolutionRequirementsTableRowOrder,
  setWorldModelSolutionRequirementsTableIsSaved,
  setWorldModelSolutionRequirementsTableDataSetStatusMap,
  setWorldModelSolutionRequirementsTableSelectedRows,

  setAdasProgramsMetaData,
  setSelectedLeftPanelTreeNode,
  setRefreshAfterSync,

  updateSensingSystemRequirementsTable,
  updateWorldModelSolutionRequirementsTable,

  setIsVehicleProgramsReadOnly,
  setIsCoverageRequirementsReadOnly
} = actions

export default reducer